import { Box, Button, CircularProgress, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExtensionOffOutlinedIcon from '@mui/icons-material/ExtensionOffOutlined';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useBrandConfig } from '../context/BrandConfigContext';
import { pageModule } from './pageRights';
import { useRights } from './useRights';

// Garde des routes protégées : applique la table pageRights à la page demandée. Côté front uniquement :
// l'API doit refuser elle-même les appels non autorisés.
function RightsGuard() {
  const { pathname } = useLocation();
  const { ready, failed, retry, canOpen } = useRights();
  const { t } = useLanguage();
  const { ready: brandReady, setting } = useBrandConfig();
  const module = pageModule(pathname);

  // Droits impossibles à charger : on ne sait pas si l'accès est autorisé, ce n'est donc pas un refus
  if (failed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, height: '60vh', px: 2, textAlign: 'center' }}>
        <ErrorOutlineIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="h6">{t('common.rights.checkFailedTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('common.rights.checkFailedText')}</Typography>
        <Button variant="outlined" onClick={retry}>{t('common.rights.retry')}</Button>
      </Box>
    );
  }

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Page liée à un module d'enseigne : on attend la configuration avant de conclure
  if (module && !brandReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (module && !setting(module)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, height: '60vh', px: 2, textAlign: 'center' }}>
        <ExtensionOffOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="h6">{t('common.rights.moduleDisabledTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('common.rights.moduleDisabledText')}</Typography>
        <Button component={Link} to="/dashboard" variant="outlined">{t('common.rights.backHome')}</Button>
      </Box>
    );
  }

  if (!canOpen(pathname)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, height: '60vh', px: 2, textAlign: 'center' }}>
        <LockOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="h6">{t('common.rights.forbiddenTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('common.rights.forbiddenText')}</Typography>
        <Button component={Link} to="/dashboard" variant="outlined">{t('common.rights.backHome')}</Button>
      </Box>
    );
  }

  return <Outlet />;
}

export default RightsGuard;
